/**
 * `state` with its routes in strip order: the routes named in `order` first, in that order, then
 * every other route in registration order. The index follows the focused route.
 *
 * Only the tab bar sees this order. The navigator keeps its fixed registration order: reordering
 * its screens on every customization would move the focused scene's native view (the Settings
 * page that is editing the strip) and churn the navigator state for a purely visual change.
 */
export function orderPrimaryNavigationStripRoutes<
  State extends { index: number; routes: { key: string; name: string }[] },
>(state: State, order: readonly string[] | undefined): State {
  if (order === undefined || order.length === 0) {
    return state;
  }
  const rank = (name: string) => {
    const index = order.indexOf(name);
    return index === -1 ? order.length : index;
  };
  const routes = state.routes
    .map((route, index) => ({ index, route }))
    .sort(
      (first, second) =>
        rank(first.route.name) - rank(second.route.name) || first.index - second.index,
    )
    .map(({ route }) => route);
  if (routes.every((route, index) => route === state.routes[index])) {
    return state;
  }
  const focusedKey = state.routes[state.index]?.key;
  return {
    ...state,
    index: Math.max(
      0,
      routes.findIndex((route) => route.key === focusedKey),
    ),
    routes,
  };
}
